'use client';

import { Button } from '@connected/ui/components/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@connected/ui/components/popover';
import {
    addHours,
    areIntervalsOverlapping,
    differenceInCalendarDays,
    differenceInMinutes,
    eachDayOfInterval,
    eachHourOfInterval,
    endOfDay,
    endOfWeek,
    format,
    getHours,
    getMinutes,
    isSameDay,
    isToday,
    startOfDay,
    startOfWeek,
} from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from '@/components/event-calendar';

import {
    DraggableEvent,
    DroppableCell,
    EventHeight,
    EventItem,
    useCurrentTimeIndicator,
    WeekCellsHeight,
} from '@/components/event-calendar';
import { EndHour, EventGap, StartHour } from '@/components/event-calendar/constants';
import { cn } from '@/lib/utils';

type WeekViewProps = {
    currentDate: Date;
    events: CalendarEvent[];
    onEventSelect: (event: CalendarEvent) => void;
    onEventCreate: (startTime: Date) => void;
    selectedEventId?: string | null;
    onBackgroundClick?: () => void;
};

type PositionedEvent = {
    event: CalendarEvent;
    top: number;
    height: number;
    left: number;
    width: number;
    zIndex: number;
};

type AllDaySegment = {
    event: CalendarEvent;
    startIndex: number;
    endIndex: number;
    span: number;
    isFirstDay: boolean;
    isLastDay: boolean;
};

type PlacedAllDaySegment = AllDaySegment & { row: number };

// Type for spanning timed events (multi-day events with specific times)
// type SpanningTimedEvent = {
//     event: CalendarEvent;
//     startIndex: number;
//     endIndex: number;
//     span: number;
//     startHour: number;
//     isFirstDay: boolean;
//     isLastDay: boolean;
// };

const DaysPerWeek = 7;

const columnStartClasses = [
    'col-start-1',
    'col-start-2',
    'col-start-3',
    'col-start-4',
    'col-start-5',
    'col-start-6',
    'col-start-7',
];

const columnSpanClasses = [
    'col-span-1',
    'col-span-2',
    'col-span-3',
    'col-span-4',
    'col-span-5',
    'col-span-6',
    'col-span-7',
];

const rowStartClasses = [
    'row-start-1',
    'row-start-2',
    'row-start-3',
    'row-start-4',
    'row-start-5',
    'row-start-6',
    'row-start-[7]',
    'row-start-[8]',
    'row-start-[9]',
    'row-start-[10]',
];

const allDayHeightClasses = [
    'min-h-[calc(var(--event-height)+var(--event-gap))]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*2)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*3)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*4)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*5)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*6)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*7)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*8)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*9)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*10)]',
];

function createAllDaySegments(
    weekDays: Date[],
    events: CalendarEvent[],
): AllDaySegment[] {
    const weekStart = startOfDay(weekDays[0]);
    const weekEnd = endOfDay(weekDays[weekDays.length - 1]);

    return events.reduce<AllDaySegment[]>((acc, event) => {
        const eventStart = startOfDay(new Date(event.start));
        const eventEnd = startOfDay(new Date(event.end ?? event.start));

        if (eventEnd < weekStart || eventStart > weekEnd)
            return acc;

        const clampedStart = eventStart < weekStart ? weekStart : eventStart;
        const clampedEnd = eventEnd > weekEnd ? weekEnd : eventEnd;

        const startIndex = Math.max(
            0,
            Math.min(DaysPerWeek - 1, differenceInCalendarDays(clampedStart, weekStart)),
        );
        const endIndex = Math.max(
            startIndex,
            Math.min(DaysPerWeek - 1, differenceInCalendarDays(clampedEnd, weekStart)),
        );
        const span = endIndex - startIndex + 1;

        acc.push({
            event,
            startIndex,
            endIndex,
            span,
            isFirstDay: isSameDay(clampedStart, eventStart),
            isLastDay: isSameDay(clampedEnd, eventEnd),
        });

        return acc;
    }, []);
}

function placeAllDaySegments(segments: AllDaySegment[]): PlacedAllDaySegment[] {
    const sorted = [...segments].sort((a, b) => {
        if (a.startIndex !== b.startIndex)
            return a.startIndex - b.startIndex;
        if (a.span !== b.span)
            return b.span - a.span;
        return new Date(a.event.start).getTime() - new Date(b.event.start).getTime();
    });

    const rowEndIndexes: number[] = [];
    const placed: PlacedAllDaySegment[] = [];

    sorted.forEach((segment) => {
        let targetRow = 0;
        while (
            rowEndIndexes[targetRow] !== undefined
            && segment.startIndex <= rowEndIndexes[targetRow]
        ) {
            targetRow++;
        }

        rowEndIndexes[targetRow] = segment.endIndex;
        placed.push({ ...segment, row: targetRow });
    });

    return placed;
}

function buildAllDayLayout(
    weekDays: Date[],
    events: CalendarEvent[],
): { placedSegments: PlacedAllDaySegment[]; rowCount: number } {
    if (weekDays.length === 0)
        return { placedSegments: [], rowCount: 0 };

    const segments = createAllDaySegments(weekDays, events);
    const placedSegments = placeAllDaySegments(segments);
    const rowCount = placedSegments.reduce(
        (max, segment) => Math.max(max, segment.row + 1),
        0,
    );

    return {
        placedSegments,
        rowCount,
    };
}

// Create spanning segments for multi-day timed events
// function createSpanningTimedEvents(
//     weekDays: Date[],
//     events: CalendarEvent[],
// ): SpanningTimedEvent[] {
//     if (weekDays.length === 0)
//         return [];

//     const weekStart = startOfDay(weekDays[0]);
//     const weekEnd = endOfDay(weekDays[weekDays.length - 1]);

//     return events.reduce<SpanningTimedEvent[]>((acc, event) => {
//         // Skip all-day events
//         if (event.allDay === true)
//             return acc;

//         const eventStart = new Date(event.start);
//         const eventEnd = new Date(event.end ?? event.start);

//         // Check if event spans multiple days
//         if (isSameDay(eventStart, eventEnd))
//             return acc;

//         // Check if event is within the week
//         if (eventEnd < weekStart || eventStart > weekEnd)
//             return acc;

//         const clampedStart = eventStart < weekStart ? weekStart : eventStart;
//         const clampedEnd = eventEnd > weekEnd ? weekEnd : eventEnd;

//         const startIndex = Math.max(
//             0,
//             Math.min(DaysPerWeek - 1, differenceInCalendarDays(startOfDay(clampedStart), weekStart)),
//         );
//         const endIndex = Math.max(
//             startIndex,
//             Math.min(DaysPerWeek - 1, differenceInCalendarDays(startOfDay(clampedEnd), weekStart)),
//         );
//         const span = endIndex - startIndex + 1;

//         // Get the start hour for positioning
//         const startHour = getHours(eventStart);

//         acc.push({
//             event,
//             startIndex,
//             endIndex,
//             span,
//             startHour,
//             isFirstDay: isSameDay(clampedStart, eventStart),
//             isLastDay: isSameDay(clampedEnd, eventEnd),
//         });

//         return acc;
//     }, []);
// }

export function WeekView({
    currentDate,
    events,
    onEventSelect,
    onEventCreate,
    selectedEventId,
    onBackgroundClick,
}: WeekViewProps) {
    const { i18n, t } = useTranslation('dashboard');
    const [internalActiveEventId, setInternalActiveEventId] = useState<string | null>(null);
    const activeEventId = selectedEventId ?? internalActiveEventId;
    const dateFnsLocale = useMemo(() => {
        const lang = i18n.language || 'en-GB';
        if (lang.startsWith('nl'))
            return nl;
        return enGB;
    }, [i18n.language]);

    const days = useMemo(() => {
        const weekStart = startOfWeek(currentDate, { locale: dateFnsLocale });
        const weekEnd = endOfWeek(currentDate, { locale: dateFnsLocale });
        return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }, [currentDate, dateFnsLocale]);

    const hours = useMemo(() => {
        const dayStart = startOfDay(currentDate);
        return eachHourOfInterval({
            start: addHours(dayStart, StartHour),
            end: addHours(dayStart, EndHour - 1),
        });
    }, [currentDate]);

    // Get only explicitly marked all-day events (events without specific times)
    const allDayEvents = useMemo(() => {
        return events
            .filter((event) => {
                // Only include explicitly marked all-day events
                return event.allDay === true;
            })
            .filter((event) => {
                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end ?? event.start);
                return days.some(
                    day =>
                        isSameDay(day, eventStart)
                        || isSameDay(day, eventEnd)
                        || (day > eventStart && day < eventEnd),
                );
            });
    }, [events, days]);

    const { placedSegments: allDaySegments, rowCount: allDayRowCount } = useMemo(
        () => buildAllDayLayout(days, allDayEvents),
        [days, allDayEvents],
    );
    const computedAllDayRows = Math.max(allDayRowCount, 1);
    const allDayHeightClass
        = allDayHeightClasses[Math.min(computedAllDayRows - 1, allDayHeightClasses.length - 1)]
            ?? allDayHeightClasses[allDayHeightClasses.length - 1];

    // // Get spanning timed events (multi-day events with specific times)
    // const spanningTimedEvents = useMemo(
    //     () => createSpanningTimedEvents(days, events),
    //     [days, events],
    // );

    // // Helper to check if an event is a multi-day timed event
    // const isMultiDayTimedEvent = (event: CalendarEvent) => {
    //     if (event.allDay === true)
    //         return false;
    //     const eventStart = new Date(event.start);
    //     const eventEnd = new Date(event.end ?? event.start);
    //     return !isSameDay(eventStart, eventEnd);
    // };

    // Process events for each day to calculate positions
    const processedDayEvents = useMemo(() => {
        const result = days.map((day) => {
            // Get events for this day that are not all-day events
            // Include multi-day timed events - they show on all their days
            const dayEvents = events.filter((event) => {
                // Skip explicitly all-day events
                if (event.allDay === true)
                    return false;

                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end ?? event.start);

                // Check if event is on this day
                return (
                    isSameDay(day, eventStart)
                    || isSameDay(day, eventEnd)
                    || (eventStart < day && eventEnd > day)
                );
            });

            // Sort events by start time and duration
            const sortedEvents = [...dayEvents].sort((a, b) => {
                const aStart = new Date(a.start);
                const bStart = new Date(b.start);
                const aEnd = new Date(a.end ?? a.start);
                const bEnd = new Date(b.end ?? b.start);

                // First sort by start time
                if (aStart < bStart)
                    return -1;
                if (aStart > bStart)
                    return 1;

                // If start times are equal, sort by duration (longer events first)
                const aDuration = differenceInMinutes(aEnd, aStart);
                const bDuration = differenceInMinutes(bEnd, bStart);
                return bDuration - aDuration;
            });

            // Group events by hour to determine which should be stacked
            const eventsByHour = new Map<number, CalendarEvent[]>();

            sortedEvents.forEach((event) => {
                const eventStart = new Date(event.start);
                // Use original start time for all days (including continuation days)
                const startHour = getHours(eventStart);

                if (!eventsByHour.has(startHour)) {
                    eventsByHour.set(startHour, []);
                }
                eventsByHour.get(startHour)!.push(event);
            });

            // Calculate positions for each event
            const positionedEvents: PositionedEvent[] = [];
            const dayStart = startOfDay(day);

            // Track columns for overlapping events
            const columns: { event: CalendarEvent; end: Date }[][] = [];

            sortedEvents.forEach((event) => {
                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end ?? event.start);

                // Adjust start and end times if they're outside this day
                // For multi-day events: keep original start time position on all days
                const isLastDay = isSameDay(day, eventEnd);

                // Use original start time for positioning on all days
                const adjustedStart = eventStart;
                const adjustedEnd = isLastDay
                    ? eventEnd
                    : addHours(dayStart, EndHour);

                // Calculate top position and height
                const startHour
                    = getHours(adjustedStart) + getMinutes(adjustedStart) / 60;
                let endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60;

                // Ensure minimum 1-hour duration for events with same start/end time
                if (endHour <= startHour) {
                    endHour = startHour + 1;
                }

                // Clamp hours to visible range
                const clampedStartHour = Math.max(startHour, StartHour);
                const clampedEndHour = Math.min(endHour, EndHour);

                // Skip if event is outside visible hours
                if (clampedStartHour >= clampedEndHour)
                    return;

                // Adjust the top calculation to account for the visible start time
                const top = (clampedStartHour - StartHour) * WeekCellsHeight;
                const height = (clampedEndHour - clampedStartHour) * WeekCellsHeight;

                // Check if there are multiple events in this hour
                const hourEvents = eventsByHour.get(getHours(adjustedStart)) || [];
                // Only stack when 2+ events (for 2: side-by-side, for 3+: compact with +more)
                // Single events get full height positioning
                const shouldStack = hourEvents.length >= 2;

                if (shouldStack) {
                    // For stacked events, use full width
                    positionedEvents.push({
                        event,
                        top,
                        height,
                        left: 0,
                        width: 1,
                        zIndex: 10,
                    });
                }
                else {
                    // Original column-based positioning for single or non-stacked events
                    // Find a column for this event
                    let columnIndex = 0;
                    let placed = false;

                    while (!placed) {
                        const col = columns[columnIndex] || [];
                        if (col.length === 0) {
                            columns[columnIndex] = col;
                            placed = true;
                        }
                        else {
                            const overlaps = col.some(c =>
                                areIntervalsOverlapping(
                                    { start: adjustedStart, end: adjustedEnd },
                                    {
                                        start: new Date(c.event.start),
                                        end: new Date(c.event.end ?? c.event.start),
                                    },
                                ),
                            );
                            if (!overlaps) {
                                placed = true;
                            }
                            else {
                                columnIndex++;
                            }
                        }
                    }

                    // Ensure column is initialized before pushing
                    const currentColumn = columns[columnIndex] || [];
                    columns[columnIndex] = currentColumn;
                    currentColumn.push({ event, end: adjustedEnd });

                    // Calculate width and left position based on number of columns
                    const width = columnIndex === 0 ? 1 : 1 - (columnIndex * 0.1);
                    const left = columnIndex === 0 ? 0 : columnIndex * 0.1;

                    positionedEvents.push({
                        event,
                        top,
                        height,
                        left,
                        width,
                        zIndex: 10 + columnIndex, // Higher columns get higher z-index
                    });
                }
            });

            return positionedEvents;
        });

        return result;
    }, [days, events]);

    // Group events by hour for each day to show stacked layout
    // Include ALL events (both same-day and multi-day) for the stacking logic
    const eventsByDayAndHour = useMemo(() => {
        const result = days.map((day) => {
            const hourGroups = new Map<number, CalendarEvent[]>();

            // Get events for this day that are not all-day events
            // Include multi-day timed events too - they should be part of stacking
            const dayEvents = events.filter((event) => {
                if (event.allDay === true)
                    return false;

                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end ?? event.start);

                return (
                    isSameDay(day, eventStart)
                    || isSameDay(day, eventEnd)
                    || (eventStart < day && eventEnd > day)
                );
            });

            dayEvents.forEach((event) => {
                const eventStart = new Date(event.start);

                // For multi-day events, keep original start time position on all days
                const startHour = Math.max(Math.floor(getHours(eventStart)), StartHour);

                // Only add if within visible hours
                if (startHour < EndHour) {
                    if (!hourGroups.has(startHour)) {
                        hourGroups.set(startHour, []);
                    }
                    hourGroups.get(startHour)!.push(event);
                }
            });

            return hourGroups;
        });

        return result;
    }, [days, events]);

    const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
        e.stopPropagation();
        setInternalActiveEventId(event.id ?? null);
        onEventSelect(event);
    };

    const clearActiveSelection = () => {
        setInternalActiveEventId(null);
        onBackgroundClick?.();
    };

    const showAllDaySection = allDayEvents.length > 0;
    const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(
        currentDate,
        'week',
    );

    return (
        <div data-slot="week-view" className="flex h-full flex-col">
            <div className="bg-background/80 border-border/70 sticky top-0 z-30 grid grid-cols-8 border-b backdrop-blur-md">
                <div className="text-muted-foreground/70 py-2 text-center text-sm">
                    <span className="max-[479px]:sr-only">{format(new Date(), 'O')}</span>
                </div>
                {days.map(day => (
                    <div
                        key={day.toString()}
                        className="data-today:text-foreground text-muted-foreground/70 py-2 text-center text-sm data-today:font-medium"
                        data-today={isToday(day) || undefined}
                    >
                        <span className="sm:hidden" aria-hidden="true">
                            {format(day, 'E', { locale: dateFnsLocale })[0]}
                            {' '}
                            {format(day, 'd', { locale: dateFnsLocale })}
                        </span>
                        <span className="max-sm:hidden">{format(day, 'EEE dd', { locale: dateFnsLocale })}</span>
                    </div>
                ))}
            </div>

            {showAllDaySection && (
                <div className="border-border/70 bg-muted/50 border-b" onClick={clearActiveSelection}>
                    <div className="grid grid-cols-8">
                        <div className={cn('border-border/70 relative border-r', allDayHeightClass)}>
                            <span className="text-muted-foreground/70 absolute bottom-0 left-0 h-6 w-16 max-w-full pe-2 text-right text-[10px] sm:pe-4 sm:text-xs">
                                {t('planning.allDay', 'No date')}
                            </span>
                        </div>
                        <div className={cn('col-span-7 relative', allDayHeightClass)}>
                            <div className={cn('grid h-full grid-cols-7', allDayHeightClass)}>
                                {days.map(day => (
                                    <div
                                        key={day.toString()}
                                        className={cn('border-border/70 relative border-r p-1 last:border-r-0', allDayHeightClass)}
                                        data-today={isToday(day) || undefined}
                                    >
                                        <span className="sr-only">
                                            {format(day, 'EEEE dd MMM', { locale: dateFnsLocale })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="pointer-events-none absolute inset-0 grid grid-cols-7 auto-rows-[var(--event-height)] gap-x-0 gap-y-[var(--event-gap)] p-1">
                                {allDaySegments.map((segment) => {
                                    const startClass = columnStartClasses[Math.min(segment.startIndex, columnStartClasses.length - 1)];
                                    const spanClass = columnSpanClasses[Math.min(segment.span, columnSpanClasses.length) - 1];
                                    const rowClass = rowStartClasses[Math.min(segment.row, rowStartClasses.length - 1)];
                                    const shouldShowContent = segment.isFirstDay || segment.startIndex === 0;

                                    return (
                                        <div
                                            key={`all-day-${segment.event.id}-${segment.row}-${segment.startIndex}`}
                                            className={`pointer-events-auto px-0.5 ${startClass} ${spanClass} ${rowClass}`}
                                        >
                                            <div className="h-full">
                                                <DraggableEvent
                                                    event={segment.event}
                                                    view="month"
                                                    onClick={e => handleEventClick(segment.event, e)}
                                                    isFirstDay={segment.isFirstDay}
                                                    isLastDay={segment.isLastDay}
                                                    hideContent={!shouldShowContent}
                                                    isSelected={segment.event.id === activeEventId}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="relative grid flex-1 grid-cols-8 overflow-hidden" onClick={clearActiveSelection}>
                <div className="border-border/70 grid auto-cols-fr border-r">
                    {hours.map((hour, index) => (
                        <div
                            key={hour.toString()}
                            className="border-border/70 relative min-h-[var(--week-cells-height)] border-b last:border-b-0"
                        >
                            {index > 0 && (
                                <span className="bg-background text-muted-foreground/70 absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                                    {format(hour, 'HH:mm')}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {days.map((day, dayIndex) => {
                    const dayHourGroups = eventsByDayAndHour[dayIndex] || new Map();

                    return (
                        <div
                            key={day.toString()}
                            className="border-border/70 relative grid auto-cols-fr border-r last:border-r-0"
                            data-today={isToday(day) || undefined}
                        >
                            {/* Current time indicator - only show for today's column */}
                            {currentTimeVisible && isToday(day) && (
                                <div
                                    className="pointer-events-none absolute right-0 left-0 z-20"
                                    style={{ top: `${currentTimePosition}%` }}
                                >
                                    <div className="relative flex items-center">
                                        <div className="bg-primary absolute -left-1 h-2 w-2 rounded-full"></div>
                                        <div className="bg-primary h-[2px] w-full"></div>
                                    </div>
                                </div>
                            )}
                            {hours.map((hour) => {
                                const hourValue = getHours(hour);
                                let hourEvents = dayHourGroups.get(hourValue) || [];
                                // Sort by start time ascending
                                hourEvents = [...hourEvents].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

                                // 2+ events = show 1 event + "+X more" button (stacked vertically like month view)
                                // 1 event = show full height to end time
                                const hasMultipleEvents = hourEvents.length >= 2;
                                const hasSingleEvent = hourEvents.length === 1;

                                return (
                                    <div
                                        key={hour.toString()}
                                        className="border-border/70 relative min-h-[var(--week-cells-height)] border-b last:border-b-0"
                                    >
                                        {/* Stacked events for hours with 2+ events (like month view) */}
                                        {hasMultipleEvents && (() => {
                                            // Calculate heights based on constants
                                            const padding = 2; // p-0.5 = 2px
                                            const gap = EventGap; // gap between rows
                                            const availableHeight = WeekCellsHeight - (padding * 2) - gap;
                                            const rowHeight = Math.floor(availableHeight / 2);

                                            const firstEvent = hourEvents[0];
                                            const eventStart = new Date(firstEvent.start);
                                            const eventEnd = new Date(firstEvent.end ?? firstEvent.start);
                                            const isFirstDay = isSameDay(day, eventStart);
                                            const isLastDay = isSameDay(day, eventEnd);

                                            return (
                                                <div className="absolute inset-0 z-10 flex flex-col p-0.5 overflow-hidden">
                                                    {/* First row: 1 visible event */}
                                                    <div style={{ height: `${rowHeight}px` }}>
                                                        <DraggableEvent
                                                            event={firstEvent}
                                                            view="month"
                                                            onClick={e => handleEventClick(firstEvent, e)}
                                                            isFirstDay={isFirstDay}
                                                            isLastDay={isLastDay}
                                                            isSelected={firstEvent.id === activeEventId}
                                                        />
                                                    </div>

                                                    {/* Second row: +more button below */}
                                                    <div style={{ height: `${rowHeight}px`, marginTop: `${gap}px` }}>
                                                        <Popover modal>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    className="focus-visible:border-ring !bg-primary-foreground focus-visible:ring-ring/50 text-muted-foreground hover:text-foreground hover:bg-muted/50 flex w-full h-full items-center justify-center overflow-hidden px-2 text-xs backdrop-blur-md transition outline-none select-none focus-visible:ring-2"
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <span>
                                                                        +
                                                                        {hourEvents.length - 1}
                                                                        {' '}
                                                                        <span className="max-sm:sr-only">{t('planning.message.moreMsgs', 'more')}</span>
                                                                    </span>
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent
                                                                align="center"
                                                                className="max-w-52 p-3"
                                                                style={
                                                                    {
                                                                        '--event-height': `${EventHeight}px`,
                                                                    } as React.CSSProperties
                                                                }
                                                            >
                                                                <div className="space-y-2">
                                                                    <div className="text-sm font-medium">
                                                                        {format(day, 'd MMM, EEEE', { locale: dateFnsLocale })}
                                                                    </div>
                                                                    <div className="space-y-1">
                                                                        {hourEvents.slice(1).map((event) => {
                                                                            const evtStart = new Date(event.start);
                                                                            const evtEnd = new Date(event.end ?? event.start);
                                                                            const evtIsFirstDay = isSameDay(day, evtStart);
                                                                            const evtIsLastDay = isSameDay(day, evtEnd);

                                                                            return (
                                                                                <EventItem
                                                                                    key={event.id}
                                                                                    onClick={e => handleEventClick(event, e)}
                                                                                    event={event}
                                                                                    view="month"
                                                                                    isFirstDay={evtIsFirstDay}
                                                                                    isLastDay={evtIsLastDay}
                                                                                    isSelected={event.id === activeEventId}
                                                                                >
                                                                                </EventItem>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Single event - show with full height to end time */}
                                        {hasSingleEvent && (processedDayEvents[dayIndex] ?? [])
                                            .filter((pe) => {
                                                const eventStart = new Date(pe.event.start);
                                                // Keep original start time for multi-day events on all days
                                                return Math.floor(getHours(eventStart)) === hourValue;
                                            })
                                            .map(positionedEvent => (
                                                <div
                                                    key={positionedEvent.event.id}
                                                    className="absolute z-10 px-0.5"
                                                    style={{
                                                        top: `${positionedEvent.top - (hourValue - StartHour) * WeekCellsHeight}px`,
                                                        height: `${positionedEvent.height}px`,
                                                        left: `${positionedEvent.left * 100}%`,
                                                        width: `${positionedEvent.width * 100}%`,
                                                        zIndex: positionedEvent.zIndex,
                                                    }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <div className="h-full w-full">
                                                        <DraggableEvent
                                                            event={positionedEvent.event}
                                                            view="week"
                                                            onClick={e => handleEventClick(positionedEvent.event, e)}
                                                            showTime
                                                            height={positionedEvent.height}
                                                            isSelected={positionedEvent.event.id === activeEventId}
                                                        />
                                                    </div>
                                                </div>
                                            ))}

                                        {/* Quarter-hour intervals */}
                                        {[0, 1, 2, 3].map((quarter) => {
                                            const quarterHourTime = hourValue + quarter * 0.25;
                                            return (
                                                <DroppableCell
                                                    key={`${hour.toString()}-${quarter}`}
                                                    id={`week-cell-${day.toISOString()}-${quarterHourTime}`}
                                                    date={day}
                                                    time={quarterHourTime}
                                                    className={cn(
                                                        'absolute h-[calc(var(--week-cells-height)/4)] w-full',
                                                        quarter === 0 && 'top-0',
                                                        quarter === 1
                                                        && 'top-[calc(var(--week-cells-height)/4)]',
                                                        quarter === 2
                                                        && 'top-[calc(var(--week-cells-height)/4*2)]',
                                                        quarter === 3
                                                        && 'top-[calc(var(--week-cells-height)/4*3)]',
                                                    )}
                                                    onClick={() => {
                                                        clearActiveSelection();
                                                        const startTime = new Date(day);
                                                        startTime.setHours(hourValue);
                                                        startTime.setMinutes(quarter * 15);
                                                        onEventCreate(startTime);
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
